"use strict";

exports.__esModule = true;
exports.Homa = void 0;
var _sdkCore = require("@acala-network/sdk-core");
var _util = require("@polkadot/util");
var _rxjs = require("rxjs");
var _operators = require("rxjs/operators");
var _getChainType = require("../utils/get-chain-type");
var _storages = require("./storages");
var _exchangeRate = require("./utils/exchange-rate");
var _getApy = require("./utils/get-apy");
var _getEstimateMintResult = require("./utils/get-estimate-mint-result");
var _getEstimateRedeemResult = require("./utils/get-estimate-redeem-result");
var _transformStakingLedger = require("./utils/transform-staking-ledger");
var _getUserLiquidTokenSummary = require("./utils/get-user-liquid-token-summary");
var _errors = require("./errors");
class Homa {
  constructor(api, wallet) {
    this.api = void 0;
    this.storages = void 0;
    this.wallet = void 0;
    this.consts = void 0;
    this.isReady$ = void 0;
    this.getTotalStakingBonded$ = (0, _util.memoize)(() => {
      const {
        stakingToken
      } = this.consts;
      return this.storages.totalStakingBonded().observable.pipe((0, _rxjs.map)(data => _sdkCore.FixedPointNumber.fromInner(data.toString(), stakingToken.decimals)), (0, _rxjs.shareReplay)(1));
    });
    this.getToBondPool$ = (0, _util.memoize)(() => {
      const {
        stakingToken
      } = this.consts;
      return this.storages.toBondPool().observable.pipe((0, _rxjs.map)(data => _sdkCore.FixedPointNumber.fromInner(data.toString(), stakingToken.decimals)), (0, _rxjs.shareReplay)(1));
    });
    this.getTotalVoidLiquid$ = (0, _util.memoize)(() => {
      return this.storages.totalVoidLiquid().observable.pipe((0, _rxjs.map)(data => _sdkCore.FixedPointNumber.fromInner(data.toString(), this.consts.liquidToken.decimals)), (0, _rxjs.shareReplay)(1));
    });
    this.getTotalLiquidity$ = (0, _util.memoize)(() => {
      return (0, _rxjs.combineLatest)({
        totalVoidLiquid: this.totalVoidLiquid$,
        totalIssuance: this.storages.issuance(this.consts.liquidToken).observable
      }).pipe((0, _rxjs.map)(_ref => {
        let {
          totalVoidLiquid,
          totalIssuance
        } = _ref;
        return _sdkCore.FixedPointNumber.fromInner(totalIssuance.toString(), this.consts.liquidToken.decimals).add(totalVoidLiquid);
      }), (0, _rxjs.shareReplay)(1));
    });
    this.getFastMatchFeeRate$ = (0, _util.memoize)(() => {
      return this.storages.fastMatchFeeRate().observable.pipe((0, _rxjs.map)(data => _sdkCore.FixedPointNumber.fromInner(data.toString())), (0, _rxjs.shareReplay)(1));
    });
    this.getCommissionRate$ = (0, _util.memoize)(() => {
      return this.storages.commissionRate().observable.pipe((0, _rxjs.map)(data => _sdkCore.FixedPointNumber.fromInner(data.toString())), (0, _rxjs.shareReplay)(1));
    });
    this.getSoftBondedCapPerSubAccount$ = (0, _util.memoize)(() => {
      return this.storages.softBondedCapPerSubAccount().observable.pipe((0, _rxjs.map)(data => _sdkCore.FixedPointNumber.fromInner(data.toString(), this.consts.stakingToken.decimals)), (0, _rxjs.shareReplay)(1));
    });
    this.getStakingLedgers$ = (0, _util.memoize)(() => {
      return this.storages.stakingLedgers().observable.pipe((0, _rxjs.map)(data => (0, _transformStakingLedger.transformStakingLedger)(data, this.consts.stakingToken)), (0, _rxjs.shareReplay)(1));
    });
    this.getEraFrequency$ = (0, _util.memoize)(() => {
      return this.storages.eraFrequency().observable.pipe((0, _rxjs.map)(data => data.toNumber()), (0, _rxjs.shareReplay)(1));
    });
    this.getEstimatedRewardRatePerEra$ = (0, _util.memoize)(() => {
      return this.storages.estimatedRewardRatePerEra().observable.pipe((0, _rxjs.map)(data => _sdkCore.FixedPointNumber.fromInner(data.toString())), (0, _rxjs.shareReplay)(1));
    });
    this.getRedeemRequest$ = (0, _util.memoize)(address => {
      return this.storages.redeemRequests(address).observable.pipe((0, _rxjs.map)(data => {
        var _data$unwrapOrDefault, _data$unwrapOrDefault2;
        return [_sdkCore.FixedPointNumber.fromInner((_data$unwrapOrDefault = data.unwrapOrDefault()) === null || _data$unwrapOrDefault === void 0 ? void 0 : _data$unwrapOrDefault[0].toString(), this.consts.liquidToken.decimals), (_data$unwrapOrDefault2 = data.unwrapOrDefault()) === null || _data$unwrapOrDefault2 === void 0 ? void 0 : _data$unwrapOrDefault2[1].isTrue];
      }), (0, _rxjs.shareReplay)(1));
    });
    this.relayChainCurrentEra$ = (0, _util.memoize)(() => {
      return this.storages.relayChainCurrentEra().observable.pipe((0, _rxjs.map)(data => data.toNumber()));
    });
    this.getUnbondings$ = (0, _util.memoize)(address => {
      return this.storages.unbondings(address).observable.pipe((0, _rxjs.map)(data => {
        return data.map(item => {
          const era = item[0].args[1].toNumber();
          return {
            era,
            amount: _sdkCore.FixedPointNumber.fromInner(item[1].toString(), this.consts.stakingToken.decimals)
          };
        }).sort((a, b) => a.era - b.era);
      }), (0, _rxjs.shareReplay)(1));
    });
    this.getEnv$ = (0, _util.memoize)(() => {
      return this.isReady$.pipe((0, _operators.filter)(i => i), (0, _rxjs.switchMap)(() => {
        return (0, _rxjs.combineLatest)({
          totalStakingBonded: this.totalStakingBonded$,
          toBondPool: this.toBondPool$,
          totalLiquidity: this.totalLiquidity$,
          estimatedRewardRatePerEra: this.estimatedRewardRatePerEra$,
          fastMatchFeeRate: this.fastMatchFeeRate$,
          commissionRate: this.commissionRate$,
          eraFrequency: this.eraFrequency$,
          softBondedCapPerSubAccount: this.softBondedCapPerSubAccount$
        }).pipe((0, _rxjs.map)(_ref2 => {
          let {
            totalStakingBonded,
            toBondPool,
            totalLiquidity,
            estimatedRewardRatePerEra,
            fastMatchFeeRate,
            commissionRate,
            eraFrequency,
            softBondedCapPerSubAccount
          } = _ref2;
          const {
            mintThreshold,
            redeemThreshold
          } = this.consts;
          const totalStaking = toBondPool.add(totalStakingBonded);
          return {
            totalStaking: totalStaking,
            totalLiquidity,
            exchangeRate: (0, _exchangeRate.getExchangeRate)(totalStaking, totalLiquidity),
            toBondPool,
            estimatedRewardRatePerEra,
            apy: (0, _getApy.getAPY)(estimatedRewardRatePerEra.toNumber(), commissionRate.toNumber(), eraFrequency, (0, _getChainType.getChainType)(this.consts.chain)),
            fastMatchFeeRate,
            commissionRate,
            eraFrequency,
            mintThreshold,
            redeemThreshold,
            stakingSoftCap: softBondedCapPerSubAccount.mul(new _sdkCore.FixedPointNumber(this.consts.activeSubAccountsIndexList.length))
          };
        }), (0, _rxjs.shareReplay)(1));
      }));
    });
    /**
     * @name convertor$
     * @description return convertLiquidToStaking and convertStakingToLiquid
     */
    this.getConvertor$ = (0, _util.memoize)(() => {
      return this.env$.pipe((0, _rxjs.map)(env => ({
        convertLiquidToStaking: data => (0, _exchangeRate.convertLiquidToStaking)(env.exchangeRate, data),
        convertStakingToLiquid: data => (0, _exchangeRate.convertStakingToLiquid)(env.exchangeRate, data)
      })));
    });
    /**
     * @name subscribleEstimateMintResult
     * @description subscrible estimate mint result
     */
    this.subscribeEstimateMintResult = (0, _util.memoize)(amount => {
      return this.env$.pipe((0, _rxjs.map)(env => (0, _getEstimateMintResult.getEstimateMintResult)(amount, env)));
    });
    this.createMintCall = (0, _util.memoize)(amount => {
      return this.api.tx.homa.mint(amount.toChainData());
    });
    this.subscribeEstimateRedeemResult = (0, _util.memoize)((amount, isFastRedeem) => {
      return this.env$.pipe((0, _rxjs.map)(env => (0, _getEstimateRedeemResult.getEstimateRedeemResult)(env, amount, isFastRedeem)));
    });
    this.createRedeemCall = (0, _util.memoize)((amount, isFastMatch, address) => {
      if (isFastMatch) {
        if (!address) throw new _errors.RequiredAddressInFastReddem();
        return this.api.tx.utility.batchAll([this.api.tx.homa.requestRedeem(amount.toChainData(), true), this.api.tx.homa.fastMatchRedeemsCompletely([address])]);
      }
      return this.api.tx.homa.requestRedeem(amount.toChainData(), false);
    });
    this.subscribeUserRedeemRequest = (0, _util.memoize)(address => {
      return (0, _rxjs.combineLatest)({
        env: this.env$,
        request: this.getRedeemRequest$(address)
      }).pipe((0, _rxjs.map)(data => {
        return {
          fastRedeem: data.request[1],
          amount: data.request[0]
        };
      }));
    });
    this.subscribeUserLiquidTokenSummary = (0, _util.memoize)(address => {
      return (0, _rxjs.combineLatest)({
        env: this.env$,
        currentEra: this.relayChainCurrentEra$(),
        redeemRequest: this.subscribeUserRedeemRequest(address),
        unbondings: this.getUnbondings$(address)
      }).pipe((0, _rxjs.map)(_ref3 => {
        let {
          env,
          currentEra,
          redeemRequest,
          unbondings
        } = _ref3;
        return (0, _getUserLiquidTokenSummary.getUserLiquidTokenSummary)(env, currentEra, unbondings, redeemRequest);
      }));
    });
    this.api = api;
    this.isReady$ = new _rxjs.BehaviorSubject(false);
    this.storages = (0, _storages.createStorages)(this.api);
    this.wallet = wallet;
    this.init();
  }
  init() {
    this.initConsts();
  }
  initConsts() {
    const liquidToken = this.wallet.getToken(this.api.consts.homa.liquidCurrencyId);
    const stakingToken = this.wallet.getToken(this.api.consts.homa.stakingCurrencyId);
    this.consts = {
      liquidToken,
      stakingToken,
      chain: this.api.runtimeChain.toString(),
      defaultExchangeRate: _sdkCore.FixedPointNumber.fromInner(this.api.consts.homa.defaultExchangeRate.toString()),
      activeSubAccountsIndexList: this.api.consts.homa.activeSubAccountsIndexList.map(item => item.toNumber()),
      mintThreshold: _sdkCore.FixedPointNumber.fromInner(this.api.consts.homa.mintThreshold.toString(), stakingToken.decimals),
      redeemThreshold: _sdkCore.FixedPointNumber.fromInner(this.api.consts.homa.redeemThreshold.toString(), stakingToken.decimals)
    };

    // set isReady to true after consts intizialized
    this.isReady$.next(true);
  }
  get isReady() {
    return (0, _rxjs.firstValueFrom)(this.isReady$.asObservable().pipe((0, _operators.filter)(i => i)));
  }
  get totalStakingBonded$() {
    return this.getTotalStakingBonded$();
  }
  get toBondPool$() {
    return this.getToBondPool$();
  }
  get totalVoidLiquid$() {
    return this.getTotalVoidLiquid$();
  }
  get totalLiquidity$() {
    return this.getTotalLiquidity$();
  }
  get fastMatchFeeRate$() {
    return this.getFastMatchFeeRate$();
  }
  get commissionRate$() {
    return this.getCommissionRate$();
  }
  get softBondedCapPerSubAccount$() {
    return this.getSoftBondedCapPerSubAccount$();
  }
  get stakingLedgers$() {
    return this.getStakingLedgers$();
  }
  get eraFrequency$() {
    return this.getEraFrequency$();
  }
  get estimatedRewardRatePerEra$() {
    return this.getEstimatedRewardRatePerEra$();
  }
  get env$() {
    return this.getEnv$();
  }
  async getEnv() {
    return (0, _rxjs.firstValueFrom)(this.env$);
  }
  get convertor$() {
    return this.getConvertor$();
  }
  async getConvertor() {
    return (0, _rxjs.firstValueFrom)(this.convertor$);
  }
  async getEstimateMintResult(amount) {
    return (0, _rxjs.firstValueFrom)(this.subscribeEstimateMintResult(amount));
  }
  async getEstimateRedeemResult(amount, isFastReddem) {
    return (0, _rxjs.firstValueFrom)(this.subscribeEstimateRedeemResult(amount, isFastReddem));
  }
  async getUserRedeemRequest(address) {
    return (0, _rxjs.firstValueFrom)(this.subscribeUserRedeemRequest(address));
  }
  async getUserLiquidTokenSummary(address) {
    return (0, _rxjs.firstValueFrom)(this.subscribeUserLiquidTokenSummary(address));
  }
}
exports.Homa = Homa;